import readings from './oudezijdeRows.json';
import type { BuildContext } from '../../types';
import type { PolygonIndex } from '../../../geo/polygonIndex';
import type { ReservedFootprint } from '../types';
import type { SurveyedRow } from './rowTypes';
import { WOODCUT_YARDS } from '../woodcutYards';
import { surveyedRowLots } from './rowLots';

export const OUDEZIJDE_ROWS = readings.rows as readonly SurveyedRow[];

/** Two blocks bounded by retained streets, not a rectangle through the church precinct. */
export function oudezijdeBlockReservations(layout: BuildContext['layout']): ReservedFootprint[] {
  const find = (id: string) => layout.streets.find((s) => s.id === id)?.centerline;
  const kerk = find('street-oudekerksplein'),
    enge = find('street-enge-kerksteeg');
  const niezel = find('street-lange-niezel'),
    hoek = find('street-heintje-hoekssteeg');
  if (
    !kerk ||
    !enge ||
    !niezel ||
    !hoek ||
    !find('street-warmoesstraat') ||
    !find('quay-oudezijds-voorburgwal-2')
  )
    return [];
  const outer = [
    enge[enge.length - 1]!,
    niezel[niezel.length - 1]!,
    niezel[0],
    ...kerk.slice(10).reverse(),
    ...enge.slice(1, -1),
  ];
  return [
    { id: 'surveyed-oudekerk-niezel', polygon: { outer, holes: [] } },
    {
      id: 'surveyed-niezel-heintje',
      polygon: {
        outer: [niezel[niezel.length - 1]!, hoek[hoek.length - 1]!, hoek[0], niezel[0]],
        holes: [],
      },
    },
  ];
}

export function planOudezijdeBlocks(
  ctx: Pick<BuildContext, 'layout' | 'site'>,
  buildable: PolygonIndex,
  reserved: readonly ReservedFootprint[]
) {
  const blocks = oudezijdeBlockReservations(ctx.layout);
  if (!blocks.length) return { lots: [], reserved: [], unresolved: [] };
  // This source locates open space approximately; do not use it as a cadastral parcel survey.
  const yards = WOODCUT_YARDS.filter((y) => y.id === 'lange-niezel-heintje-hoek').map(
    (y): ReservedFootprint => ({
      id: `woodcut-yard:${y.id}`,
      polygon: { outer: y.ring, holes: [] },
    })
  );
  const lots = surveyedRowLots(
    OUDEZIJDE_ROWS,
    ctx.layout,
    buildable,
    [...reserved, ...yards],
    20_000
  );
  const built = new Set(lots.map((l) => l.reconstruction!.id));
  const unresolved = OUDEZIJDE_ROWS.flatMap((r) =>
    r.houses.filter((h) => !built.has(h.id)).map((h) => h.id)
  );
  const footprints = lots.map((l): ReservedFootprint => ({
    id: `surveyed:${l.reconstruction!.id}`,
    polygon: { outer: [...l.corners], holes: [] },
  }));
  return { lots, reserved: [...blocks, ...yards, ...footprints], unresolved };
}
